import {
    faCompactDisc,
    faHeadphones,
    faKeyboard,
    faMicrochip,
    faVideo,
} from '@fortawesome/free-solid-svg-icons';
import type { Heading } from '../types/global';
import type { ToolGroup } from '../types/tools';

export const toolHeading: Heading = {
    heading: 'Studio gear & tools',
    paragraph: "A look under the hood at the converters, preamps, monitoring, instruments, and tooling that drive sessions at Strangeways. Click a card for a more detailed breakdown of the signal chain in each area."
}

export const toolGroups: ToolGroup[] = [
    {
        id: 'audio-conversion',
        icon: faMicrochip,
        title: 'Audio & conversion',
        summary:
            '32 channels of clean I/O built around Antelope conversion and Trident-flavoured preamps, clocked for low jitter and phase coherence.',
        details: [
            'Antelope Audio Orion Studio Synergy Core as the primary interface and converter, feeding 12 analogue inputs with onboard FPGA FX.',
            'Audient ASP800 and ASP880 expansion for a further 16 channels, each with Trident console preamps for subtle console-style saturation.',
            'Typical tracking format of 48 kHz / 24-bit with a mastering-grade signal path and noise floor down towards -130 dB.',
            'Mac Pro (Rack, 2019) running macOS Ventura, 3.2 GHz 16-core Intel Xeon W, 96 GB DDR4, and PCIe NVMe storage for heavy multitrack sessions and video capture.',
        ],
    },
    {
        id: 'monitoring-mics',
        icon: faHeadphones,
        title: 'Monitoring & microphones',
        summary:
            'Four discrete stereo cue mixes and a mic locker spanning dynamics, condensers, ribbons, and modelling mics for most sources.',
        details: [
            'Cranborne Audio N8 system providing four individual stereo headphone sends with analogue summing and talkback.',
            'Yorkville NX55P wedges plus sub forming a small PA, used for rehearsal monitoring and live-off-the-floor stage feel.',
            'Focal Alpha 50 nearfields for mix position, with Audio-Technica ATH-M50x and Sennheiser HD 280 Pro headphones for alternate perspectives.',
            'Mic locker highlights: Shure SM57/58/SM7B/Beta dynamics, Sennheiser MD421-II and MD441-U, AKG C451E pair, Røde large/small diaphragm condensers, Electrovoice 635a, sE Z3300A, and Antelope Edge Solo/Edge Note modelling mics.',
        ],
    },
    {
        id: 'instruments-keys',
        icon: faKeyboard,
        title: 'Instruments & keyboards',
        summary:
            'A mix of modern MPE controllers, analogue/digital synths, and classic-style keys for building full arrangements in the room.',
        details: [
            'Expressive E Osmose for MPE-driven performance (poly-aftertouch, per-note pitch, and pressure control) tied into the DAW or outboard synths.',
            'Elektron Analog Keys and Analog Rytm handling analogue poly, drums, and sample layering with parameter locks and internal FX.',
            'Nord Electro 5D 73 and Yamaha Electone D85 covering organ, EP, and classic keyboard duties, with a Yamaha S90ES motif available as an additional workstation.',
            'Taye acoustic kit on-site plus a rotating selection of guitar and bass amps; bring your own heads/pedals or plug straight into modelling chains.',
        ],
    },
    {
        id: 'software-daws',
        icon: faCompactDisc,
        title: 'Software, DAWs & processing',
        summary:
            'Logic, Ableton, and Reaper at the core, with a plugin stack tuned for production, surgical work, and mastering.',
        details: [
            'Primary DAWs: Logic Pro X 11.2.2, Ableton Live 11, and Reaper 7.x; Pro Tools available on a per-project basis if required.',
            'Software instruments include Spitfire BBC Symphony Orchestra Core, PolyMAX, and a curated set of synths/samplers for modern scoring and production.',
            'Processing chain featuring iZotope Ozone 11, Waves Abbey Road suites, Soundtoys, and a variety of Antelope Audio and UAD channel strips, compressors, EQs, and tape/saturation tools.',
            'Projects handed off via Nextcloud with per-client accounts so sessions, stems, and mixes can be synced directly to your own system.',
        ],
    },
    {
        id: 'video-remote',
        icon: faVideo,
        title: 'Video & remote sessions',
        summary:
            'Broadcast-grade SDI ingest and Sessionwire routing for remote direction, mix attendance, and on-location capture.',
        details: [
            'Blackmagic Decklink 8K Pro providing four SDI inputs plus monitor out, fed by HDMI-to-SDI converters for flexible camera choices.',
            'Default rig of four GoPro Hero4 Silver cameras, with the option to integrate third-party HDMI/SDI cameras up to 8K resolution.',
            'Sessionwire used for assignable two-way high-quality audio streams and HD video between studios or remote listeners, including mix attendance.',
            'Entire capture chain (Mac Pro, converters, monitoring, and Blackmagic I/O) housed in a portable road case for location recording and broadcast.',
        ],
    },
]